import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, Switch, Typography } from '@mui/material';
import { GamifyToast } from '../../common/CustomToasts';
import { API } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';

export default function ToggleRecordingConsentModal({
  showModal,
  setShowModal,
  circle,
  onSaved,
}) {
  const [saving, setSaving] = useState(false);
  // Treat null/undefined as enabled (true) by default
  const [useRecordingConsent, setUseRecordingConsent] = useState(
    !!circle?.useRecordingConsent
  );

  // Sync state when switching teams
  useEffect(() => {
    setUseRecordingConsent(circle?.useRecordingConsent ?? true);
  }, [circle?.id]);

  const handleClose = () => {
    if (!saving) setShowModal(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await API.graphql({
        query: mutations.updateCircle,
        variables: {
          input: {
            id: circle.id,
            useRecordingConsent,
          },
        },
      });
      GamifyToast.success(
        useRecordingConsent
          ? 'Recording consent enabled for this team'
          : 'Recording consent disabled for this team'
      );
      setShowModal(false);
      onSaved && onSaved();
    } catch (err) {
      GamifyToast.error('Failed to update recording consent');
      // eslint-disable-next-line no-console
      console.error('ToggleRecordingConsentModal save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={!!showModal} onClose={handleClose} sx={{ zIndex: 100002 }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 480,
          bgcolor: (theme) => theme.custom.background2,
          border: (theme) => `1px solid ${theme.custom.stroke}`,
          borderRadius: '8px',
          boxShadow: '0px 4px 24px 0px #ABABAB5E',
          p: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 700,
            mb: 1,
            color: (t) => t.custom.text,
          }}
        >
          Recording Consent
        </Typography>
        <Typography sx={{ fontSize: 14, color: (t) => t.custom.text, mb: 2 }}>
          Toggle the recording consent screen for team &quot;{circle?.name}
          &quot;.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Switch
            checked={useRecordingConsent}
            onChange={(e) => setUseRecordingConsent(e.target.checked)}
            inputProps={{ 'aria-label': 'use recording consent switch' }}
          />
          <Typography sx={{ ml: 1, color: (t) => t.custom.text }}>
            {useRecordingConsent ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              color: (t) => t.custom.text,
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: '#FF6A00',
              color: 'white',
              '&:hover': { backgroundColor: '#FF6A00' },
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
